import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { crypto as cryptoShared, type infra } from '@photo-vault/shared';
import type { dto } from '@photo-vault/shared';
import { fromBase64Url } from '@salvobee/crypto-vault';
import {
  AntiAutofillHoneypot,
  Button,
  Card,
  Icon,
  Input,
  PageHeader,
  ProviderIcon,
} from '../../ui/index.js';
import { useAuth } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';
import { config } from '../../lib/config.js';
import { stashRecoveryCode } from './recoveryStorage.js';

// Forgot-passphrase flow, as a single component on a public route.
//
// It is public because the user cannot build an AuthSession: they can sign in to
// Cognito, but they cannot derive the master key, so there is no session to
// guard. The three steps live in one component on purpose — between step 2 and
// step 3 we hold the unwrapped RSA private key in memory, and routing across
// pages would mean parking it somewhere persistent.
//
// Nothing is written server-side until step 3 succeeds: abandoning halfway
// leaves the old passphrase and the old recovery code fully working.

type Step = 'identify' | 'code' | 'passphrase';
type Busy = 'none' | 'signing-in' | 'unwrapping' | 'rewrapping' | 'persisting';

interface Identified {
  jwt: string;
  provider: infra.AuthProviderKind;
}

interface Unlocked {
  bootstrap: dto.RecoveryBootstrapResponse;
  /** Proof of knowledge of the current recovery code, for the conditional write. */
  currentVerifierRecoveryB64: string;
}

export function RecoverPassphrasePage() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const { auth, metadata } = useInfra();

  const [step, setStep] = useState<Step>('identify');
  const [busy, setBusy] = useState<Busy>('none');
  const [error, setError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [newPassphrase, setNewPassphrase] = useState('');
  const [newConfirm, setNewConfirm] = useState('');

  const [identified, setIdentified] = useState<Identified | null>(null);
  const [unlocked, setUnlocked] = useState<Unlocked | null>(null);
  // The recovered private key never leaves this ref: no context, no storage.
  // It dies on unmount, which is why a reload restarts the wizard.
  const rsaPrivRef = useRef<CryptoKey | null>(null);

  // A user who signed in with Google/Apple already has a JWT in session storage;
  // there is no Cognito password to ask for, so skip straight to the code.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const federated = await auth.getFederatedUser();
        if (cancelled || !federated) return;
        setIdentified({ jwt: federated.idToken, provider: 'federated' });
        setEmail(federated.email);
        setStep('code');
      } catch {
        // No federated session to reuse — the native form stays.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth]);

  async function handleIdentify(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy('signing-in');
    try {
      const cognito = await auth.signIn(email, password);
      setIdentified({ jwt: cognito.idToken, provider: 'native' });
      setStep('code');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Accesso fallito.');
    } finally {
      setBusy('none');
    }
  }

  async function handleCode(event: React.FormEvent) {
    event.preventDefault();
    if (!identified) return;
    setError(null);
    setBusy('unwrapping');
    try {
      const bootstrap = await metadata.recoveryBootstrap(identified.jwt);
      const normalised = cryptoShared.normaliseRecoveryCode(recoveryCode);
      const { masterKey, verifierB64 } = await cryptoShared.deriveMaster(
        normalised,
        fromBase64Url(bootstrap.saltRecoveryB64),
      );
      // The AES-GCM auth tag is the authoritative check: a wrong code throws here.
      const rsaPriv = await cryptoShared.unwrapPrivateKey(bootstrap.wrappedPrivRecovery, masterKey);
      rsaPrivRef.current = rsaPriv;
      setUnlocked({ bootstrap, currentVerifierRecoveryB64: verifierB64 });
      setRecoveryCode('');
      setStep('passphrase');
    } catch {
      setError('Codice di recupero non valido. Controlla di averlo copiato per intero.');
    } finally {
      setBusy('none');
    }
  }

  async function handleNewPassphrase(event: React.FormEvent) {
    event.preventDefault();
    const rsaPriv = rsaPrivRef.current;
    if (!identified || !unlocked || !rsaPriv) return;
    setError(null);

    if (newPassphrase !== newConfirm) {
      setError('Le due passphrase non coincidono.');
      return;
    }
    if (newPassphrase.length < 12) {
      setError('La nuova passphrase deve essere di almeno 12 caratteri.');
      return;
    }

    try {
      setBusy('rewrapping');
      const rebuilt = await cryptoShared.rebuildKeyMaterialForNewPassphrase(rsaPriv, newPassphrase);
      // Always rotate: the write that restores access also burns the code that
      // was just used, so a leaked code is not a permanent backdoor.
      const recovery = await cryptoShared.rebuildRecoveryMaterial(rsaPriv);

      setBusy('persisting');
      await metadata.changePassphrase(
        {
          saltB64: rebuilt.saltB64,
          wrappedPriv: rebuilt.wrappedPriv,
          verifierB64: rebuilt.verifierB64,
          saltRecoveryB64: recovery.saltRecoveryB64,
          wrappedPrivRecovery: recovery.wrappedPrivRecovery,
          verifierRecoveryB64: recovery.verifierRecoveryB64,
          proof: {
            kind: 'recovery',
            currentVerifierRecoveryB64: unlocked.currentVerifierRecoveryB64,
          },
        } satisfies dto.ChangePassphraseRequest,
        identified.jwt,
      );

      // Only past this point does the new material actually exist on the server,
      // so only now do we open a session and show the new code.
      setSession({
        userId: unlocked.bootstrap.userId,
        email: unlocked.bootstrap.email,
        jwt: identified.jwt,
        masterKey: rebuilt.masterKey,
        rsaPriv,
        rsaPubB64: unlocked.bootstrap.rsaPubB64,
        saltB64: rebuilt.saltB64,
        provider: identified.provider,
      });
      stashRecoveryCode(recovery.recoveryCode, 'recovered');
      navigate('/recovery-code', { replace: true });
    } catch (err) {
      setBusy('none');
      setError(
        err instanceof Error
          ? err.message
          : 'Ripristino fallito. Il vecchio codice potrebbe essere già stato usato.',
      );
    }
  }

  const working = busy !== 'none';

  return (
    <div className="flex w-full max-w-md flex-col gap-7">
      <PageHeader
        eyebrow="recupero"
        title={
          <>
            Ho perso la <span className="accent-text">passphrase</span>
          </>
        }
        description={DESCRIPTIONS[step]}
        align="center"
      />

      <Card className="flex flex-col gap-5 sm:p-7">
        {step === 'identify' ? (
          <>
            {config.cognitoHostedDomain ? (
              <div className="flex flex-col gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  fullWidth
                  leftIcon={<ProviderIcon provider="google" />}
                  onClick={() => auth.startFederatedLogin('Google')}
                >
                  Continua con Google
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  fullWidth
                  leftIcon={<ProviderIcon provider="apple" />}
                  onClick={() => auth.startFederatedLogin('SignInWithApple')}
                >
                  Continua con Apple
                </Button>
                <div className="flex items-center gap-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-on-surface-variant">
                  <span className="h-px flex-1 bg-outline" />
                  oppure email
                  <span className="h-px flex-1 bg-outline" />
                </div>
              </div>
            ) : null}
            <form className="flex flex-col gap-3" onSubmit={handleIdentify} autoComplete="off">
              <AntiAutofillHoneypot />
              <Input
                type="email"
                label="Email"
                placeholder="me@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                leftIcon={<Icon name="alternate_email" className="text-base" />}
                autoComplete="email"
                required
              />
              <Input
                type="password"
                label="Password Cognito"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                leftIcon={<Icon name="key" className="text-base" />}
                autoComplete="current-password"
                required
                hint="La password dell'account, non la passphrase E2EE."
              />
              {error ? <ErrorBox message={error} /> : null}
              <Button type="submit" size="lg" fullWidth loading={working}>
                {working ? 'Accedo…' : 'Continua'}
              </Button>
            </form>
          </>
        ) : null}

        {step === 'code' ? (
          <form className="flex flex-col gap-3" onSubmit={handleCode} autoComplete="off">
            <AntiAutofillHoneypot />
            <Input
              label="Codice di recupero"
              placeholder="XXXX-XXXX-XXXX-…"
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.target.value)}
              leftIcon={<Icon name="password" className="text-base" />}
              autoComplete="off"
              spellCheck={false}
              required
              hint="Trattini e maiuscole non contano: incollalo come l'hai salvato."
            />
            {error ? <ErrorBox message={error} /> : null}
            <Button type="submit" size="lg" fullWidth loading={working}>
              {working ? 'Verifico il codice…' : 'Sblocca le chiavi'}
            </Button>
          </form>
        ) : null}

        {step === 'passphrase' ? (
          <form className="flex flex-col gap-3" onSubmit={handleNewPassphrase} autoComplete="off">
            <AntiAutofillHoneypot />
            <Input
              type="password"
              label="Nuova passphrase"
              placeholder="min. 12 caratteri, meglio se ≥ 6 parole"
              value={newPassphrase}
              onChange={(e) => setNewPassphrase(e.target.value)}
              leftIcon={<Icon name="lock" className="text-base" />}
              autoComplete="new-password"
              required
            />
            <Input
              type="password"
              label="Conferma nuova passphrase"
              value={newConfirm}
              onChange={(e) => setNewConfirm(e.target.value)}
              leftIcon={<Icon name="check" className="text-base" />}
              autoComplete="new-password"
              required
            />
            {error ? <ErrorBox message={error} /> : null}
            <Button
              type="submit"
              size="lg"
              fullWidth
              loading={working}
              rightIcon={!working ? <Icon name="arrow_forward" className="text-base" /> : null}
            >
              {working ? 'Ricifro le chiavi…' : 'Imposta la nuova passphrase'}
            </Button>
          </form>
        ) : null}

        <p className="pt-1 text-center text-sm text-on-surface-variant">
          <button
            type="button"
            className="font-semibold text-cyan transition-colors hover:underline"
            onClick={() => navigate('/login')}
            disabled={working}
          >
            Torna al login
          </button>
        </p>
      </Card>
    </div>
  );
}

const DESCRIPTIONS: Record<Step, string> = {
  identify:
    'Il codice di recupero sblocca le chiavi, non l’account: per prima cosa accedi come al solito.',
  code: 'Incolla il codice di recupero che hai salvato alla registrazione. Serve a decifrare la tua chiave privata: non lasciando mai il dispositivo, il server non può aiutarti se lo hai perso.',
  passphrase:
    'Chiavi recuperate. Scegli una nuova passphrase: la coppia RSA non cambia, quindi album e condivisioni restano intatti.',
};

function ErrorBox({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-error/60 bg-error-container/30 px-4 py-3 text-sm text-error">
      {message}
    </div>
  );
}
